// Renouvellement du jeton lié au cycle de vie de l'app native.
//
// POURQUOI CE MODULE EXISTE
// -------------------------
// `supabase.auth.startAutoRefresh()` / `stopAutoRefresh()` n'étaient appelés
// NULLE PART dans le dépôt. Avec `autoRefreshToken: true`, `supabase-js`
// s'appuie sur une minuterie web, que iOS et Android suspendent dès que l'app
// passe en arrière-plan. À la réouverture, le jeton peut être périmé sans
// qu'aucun renouvellement n'ait tourné : les premières requêtes du montage
// d'écran partent alors sans jeton valable.
//
// C'est cohérent avec la forme des deux pics mesurés le 2026-08-23 : 11:04 à
// 11:06 et 18:50, chacun tenant en une à trois minutes, tous deux des montages
// de calendrier. ⚠️ Cohérent n'est pas prouvé : les requêtes en 401 ne portent
// aucun jeton, donc rien ne les rattache à un appareil.
//
// Ce module réduit la FRÉQUENCE des rafales. Il ne les rend pas impossibles :
// c'est la porte (`sessionGuardedFetch.ts`) qui garantit qu'aucune requête ne
// part en anonyme. Les deux ne se remplacent pas.
//
// Le motif d'écoute (addListener asynchrone, garde `cancelled`, `remove()` au
// démontage) est repris tel quel de `src/hooks/useSessionValidation.ts:189-213`.

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { touchNativeLastUsed } from '@/lib/auth/nativeInactivity';

/**
 * Démarre le suivi. Rend la fonction de démontage.
 * Sur le web, c'est un no-op : la minuterie du navigateur suffit.
 */
export function startNativeAutoRefresh(): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  let cancelled = false;
  let removeListener: (() => void) | undefined;

  // Au montage : l'app vient peut-être de revenir d'un long arrière-plan.
  // `startAutoRefresh()` renouvelle tout de suite si le jeton est proche de
  // l'expiration, en plus de réarmer la minuterie.
  //
  // ⚠️ Pas de `touchNativeLastUsed()` ici. `AuthProvider` appelle, au même montage,
  // `hasExceededInactivityWindow()` (qui LIT `flowcut-last-used-at`) dans un effet
  // séparé. Les deux couraient l'un contre l'autre : si l'écriture d'ici gagnait la
  // course, la garde des 6 mois lisait un horodatage tout frais et ne se
  // déclenchait plus jamais, en silence. `hasExceededInactivityWindow()` pose
  // maintenant elle-même le poinçon après avoir rendu son verdict
  // (`nativeInactivity.ts`) : lecture et écriture sont une seule opération, dans un
  // seul module, dans un ordre qui ne peut plus s'inverser.
  void supabase.auth.startAutoRefresh();

  CapacitorApp.addListener('appStateChange', (state) => {
    if (state.isActive) {
      void supabase.auth.startAutoRefresh();
      void touchNativeLastUsed();
    } else {
      void supabase.auth.stopAutoRefresh();
    }
  }).then((listener) => {
    // Le démontage peut arriver AVANT que l'enregistrement ne résolve.
    if (cancelled) listener.remove();
    else removeListener = () => listener.remove();
  });

  return () => {
    cancelled = true;
    removeListener?.();
  };
}
